import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Icon, IconButton, Text, VStack} from '@chakra-ui/react';
import {useApi} from "@/uses/useApi";
import useScreenState from "@/uses/useScreenState";
import {
    ArticleTagBody,
    ArticleTagBodySkeleton,
    CreateOrEditArticleTag,
    EntityListBody,
    EntityListBodySkeleton,
    EntityListHeader,
    EntityListHeaderSkeleton,
    EntityModalContent,
    ListHeader,
    Paginator,
    PaginatorSkeleton
} from "@/components";
import {useModal} from "@/contexts/modalContext";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useLanguage} from "@/contexts/languageContext";
import objectUtil from "@/utils/objectUtil";
import {Plus} from 'lucide-react';
import colorUtil from "@/utils/colorUtil";

const PER_PAGE = 20;

export const ArticleTagList = () => {

    const navigate = useNavigate();
    const {language, availableLanguages, t} = useLanguage();
    const sizes = {base: '95%', md: '95%', lg: '90%'};
    const api = useApi();
    const [entities, setEntities] = useState([]);

    const {open, close} = useModal();
    const [itemNumber, setItemNumber] = useState(null);

    const [currentQueryParameters, setSearchParams] = useSearchParams();
    const getDefaultState = useCallback((pageParams) => {
        const result = {}

        /** Retrieve search param */
        result.search = pageParams.get('search') ?? '';

        /** Retrieve page param */
        const parsedPage = parseInt(pageParams.get('page'), 10);
        if (isNaN(parsedPage) || parsedPage <= 0) {
            result.page = 0;
        } else {
            result.page = parsedPage - 1;
        }
        return result;
    }, []);
    const defaultParams = getDefaultState(currentQueryParameters);
    const [getParams, setGetParams] = useState({
        ...(defaultParams.page > 0 ? {page: defaultParams.page} : {}),
        ...(defaultParams.search && defaultParams.search.length >= process.env.REACT_APP_MIN_SEARCH_LENGTH ? {search: defaultParams.search} : {}),
    });

    const {state, realState, isMounted, setState} = useScreenState();

    const reqParams = useRef(null);
    const getPage = useCallback(() => {
        /** If page is different, it's only a pagination action,
         * so no need to show skeleton for paginator
         */
        const newState = reqParams.current !== null &&
        objectUtil
            .difference(getParams, reqParams.current ?? {})
            .filter((e) => !['page'].includes(e))
            .length !== 0
            ? 1 : 0;
        setState((prevState) => prevState === -1 ? prevState : newState);
        reqParams.current = getParams
        return api.ApiService.get('tag', {
            ...reqParams.current,
            limit: PER_PAGE,
            locale: language,
            ...(state === -1 ? {needFilters: 'bool:true'} : {})
        })
            .then(res => {
                if (res?.data?.type === 'success') {
                    setItemNumber(() => res?.data?.value?.estimatedTotalHits);
                    setEntities(() => (res?.data?.value?.hits ?? []));
                }
            })
            .catch(e => console.log(e?.response?.data))
            .finally(() => setState(_ => 2))
    }, [getParams, state]);

    useEffect(() => {
        getPage().then(() => {
            /** Manage persistent state of search query */
            setSearchParams(prev => {
                if ('search' in getParams) {
                    prev.set('search', getParams.search);
                } else {
                    prev.delete('search');
                }

                if ('page' in getParams) {
                    prev.set('page', (getParams.page + 1).toString());
                } else {
                    prev.delete('page');
                }

                return prev;
            })
        })
    }, [getParams]);

    const hasData = useMemo(() => {
        return isMounted && (entities.length > 0 || Object.keys(getParams).length > 0)
    }, [isMounted, entities, getParams]);

    const noDataText = t('sentences.noEntityYet.tag');

    const Table = useMemo(() => {
        return hasData
            ?
            <VStack position="relative" w="100%" overflow="hidden" flex={1} pb="2vh">
                <EntityListHeader
                    after={<IconButton
                        bg={"secondary.500"}
                        onClick={(_) => {
                            open(<EntityModalContent
                                title={t('actions.add')}
                                Body={<CreateOrEditArticleTag
                                    successCallback={() => {
                                        navigate(0);
                                    }}
                                />}
                                close={close}
                            />, 'cover')
                        }}
                    >
                        <Icon as={Plus}/>
                    </IconButton>}
                    setGetParams={setGetParams}
                    filters={{}}
                    defaultParams={defaultParams}
                    sizes={sizes}
                />
                <VStack
                    flex={1}
                    w={sizes}
                    py="1rem"
                    align="center"
                    justify="flex-start"
                    overflow="auto"
                    color="secondary.500"
                >
                    {
                        [0, 1].includes(realState)
                            ? <EntityListBodySkeleton
                                Body={ArticleTagBodySkeleton}
                                sizes={sizes}/>
                            : <EntityListBody
                                noDataText={noDataText}
                                entity="tag"
                                Body={ArticleTagBody}
                                EditBody={CreateOrEditArticleTag}
                                sizes={sizes}
                                entities={entities}
                                setEntities={setEntities}
                                getDeletionText={(e) => `"${e?.[`${language}Label`]}"`.slice(0,200)}
                            />
                    }
                </VStack>
                {
                    realState === 1
                        ? <PaginatorSkeleton/>
                        : <Paginator
                            defaultPage={defaultParams.page}
                            setGetParams={setGetParams}
                            itemNumber={itemNumber}
                            perPage={PER_PAGE}
                        />
                }
            </VStack>
            :
            <VStack align="center" justify="center">
                <Text>{noDataText}</Text>
                <IconButton
                    bg={'secondary.500'}
                    onClick={(_) => {
                        open(<EntityModalContent
                            title={t('actions.add')}
                            Body={<CreateOrEditArticleTag
                                successCallback={() => {
                                    navigate(0);
                                }}
                            />}
                            close={close}
                        />, 'cover')
                    }}
                >
                    <Icon as={Plus}/>
                </IconButton>
            </VStack>
    }, [hasData, realState, entities, setEntities, setGetParams, defaultParams, sizes, itemNumber, close, navigate])

    const Content = useMemo(() => {
        return isMounted
            ? Table
            : <VStack w="100%" overflow="hidden" flex={1} pb="2vh">
                <EntityListHeaderSkeleton
                    canSort={false}
                    hasFilters={false}
                    sizes={sizes}
                    after={<IconButton/>}
                />
                <VStack
                    flex={1}
                    w={sizes}
                    py="1rem"
                    align="center"
                    justify="flex-start"
                    overflow="auto"
                    color="secondary.500"
                >
                    <EntityListBodySkeleton
                        Body={ArticleTagBodySkeleton}
                        sizes={sizes}/>
                </VStack>
                <PaginatorSkeleton/>
            </VStack>
    }, [isMounted, Table])

    return (
        <VStack w="100%" overflow="hidden">
            <ListHeader title={t('sentences.managementOfEntities', { entity: t('objects.tag_other') })} description={t('sentences.manageEntities', { action1: t('actions.add'), action2: t('actions.edit'), action3: t('actions.view'), entity: t('objects.tag_other')})}/>
            {
                /** Wait for options to be retrieved otherwise, default state isn't took in count */
                Content
            }
        </VStack>
    );
};

export default ArticleTagList;
